import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import {
  getModules,
  getChatEfficiency,
  getChatEfficiencyTimeline,
} from '../lib/apiClient';

// ── Small presentational helpers ─────────────────────────────────────────
const StatCard = ({ label, value, sub, testid }) => (
  <div className="card p-5" data-testid={testid}>
    <div className="text-xs uppercase font-semibold tracking-wider text-gray-500">
      {label}
    </div>
    <div className="text-3xl font-bold text-gray-900 mt-1">{value}</div>
    {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
  </div>
);

const ScopeBar = ({ d, small = false }) => {
  // d = { topic_pct, course_pct, training_pct }
  const segs = [
    { key: 'topic', pct: d.topic_pct, color: '#00853E', label: 'Topic' },
    { key: 'course', pct: d.course_pct, color: '#1f6feb', label: 'Course' },
    { key: 'training', pct: d.training_pct, color: '#a371f7', label: 'Training' },
  ];
  return (
    <div
      className={`flex w-full rounded overflow-hidden border border-gray-200 ${small ? 'h-2' : 'h-3'}`}
      title={`Topic ${d.topic_pct}% · Course ${d.course_pct}% · Training ${d.training_pct}%`}
    >
      {segs.map((s) => (
        <div
          key={s.key}
          style={{ width: `${s.pct}%`, background: s.color }}
          aria-label={`${s.label} ${s.pct}%`}
        />
      ))}
    </div>
  );
};

const ScopeLegend = () => (
  <div className="flex gap-4 text-[11px] text-gray-600">
    <span className="flex items-center gap-1">
      <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: '#00853E' }} /> Topic
    </span>
    <span className="flex items-center gap-1">
      <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: '#1f6feb' }} /> Course
    </span>
    <span className="flex items-center gap-1">
      <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: '#a371f7' }} /> Training
    </span>
  </div>
);

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch (_) {
    return iso;
  }
};

// ── Drill-down modal ─────────────────────────────────────────────────────
const TimelineModal = ({ email, moduleId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    getChatEfficiencyTimeline(email, { module_id: moduleId || undefined, limit: 50 })
      .then(setData)
      .finally(() => setLoading(false));
  }, [email, moduleId]);

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="timeline-modal"
    >
      <div
        className="bg-white rounded-lg max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Recent turns</div>
            <div className="text-lg font-bold text-gray-900">{email}</div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-900 text-2xl leading-none"
            data-testid="timeline-close-btn"
          >
            ×
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto flex-1">
          {loading && <div className="text-gray-500">Loading…</div>}
          {!loading && data && data.messages.length === 0 && (
            <div className="text-gray-500">No messages in this scope.</div>
          )}
          {!loading && data && data.messages.length > 0 && (
            <ul className="space-y-3">
              {data.messages.slice().reverse().map((m, idx) => (
                <li
                  key={idx}
                  className={`p-3 rounded border ${
                    m.role === 'user'
                      ? 'bg-gray-50 border-gray-200'
                      : 'bg-emerald-50 border-emerald-200'
                  }`}
                  data-testid={`timeline-row-${idx}`}
                >
                  <div className="flex items-center gap-2 text-[11px] text-gray-600 mb-1 flex-wrap">
                    <span className="font-bold uppercase">
                      {m.role === 'user' ? 'Student' : 'AI Tutor'}
                    </span>
                    <span>· {fmtDateTime(m.created_at)}</span>
                    {m.scope && (
                      <span className="px-1.5 py-0.5 rounded bg-white border border-gray-300">
                        scope: {m.scope}
                      </span>
                    )}
                    {m.cached && (
                      <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300">
                        ⚡ cached
                      </span>
                    )}
                    {m.rag_used && (
                      <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-300">
                        RAG · {m.rag_chunk_count || 0} chunks
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-800 whitespace-pre-wrap">
                    {m.content}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main page ────────────────────────────────────────────────────────────
const ChatEfficiencyDashboard = () => {
  const [modules, setModules] = useState([]);
  const [moduleId, setModuleId] = useState('');
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [sortBy, setSortBy] = useState('messages');
  const [sortDir, setSortDir] = useState('desc');
  const [drillEmail, setDrillEmail] = useState(null);

  useEffect(() => {
    getModules().then(setModules).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setErr('');
    const params = { days };
    if (moduleId) params.module_id = moduleId;
    getChatEfficiency(params)
      .then(setData)
      .catch((e) => setErr(e.response?.data?.detail || 'Failed to load analytics'))
      .finally(() => setLoading(false));
  }, [moduleId, days]);

  const sortedStudents = useMemo(() => {
    if (!data?.students) return [];
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...data.students].sort((a, b) => {
      const av = a[sortBy];
      const bv = b[sortBy];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [data, sortBy, sortDir]);

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('desc');
    }
  };

  const SortHeader = ({ id, children, align = 'left' }) => (
    <th
      onClick={() => handleSort(id)}
      className={`px-3 py-2 cursor-pointer select-none text-${align} text-xs font-semibold uppercase tracking-wider text-gray-600 hover:text-gray-900`}
      data-testid={`sort-${id}`}
    >
      {children}
      {sortBy === id && (
        <span className="ml-1 text-gray-400">{sortDir === 'asc' ? '▲' : '▼'}</span>
      )}
    </th>
  );

  const cohort = data?.cohort;

  return (
    <div className="min-h-screen" data-testid="chat-efficiency-dashboard">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-8 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-emerald-700">
              Patent Claim 21 · Learning Signal
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mt-1">Chat-Efficiency Dashboard</h1>
            <p className="text-gray-500 mt-1">
              Engagement profile per student: scope mix, question depth, repeat-rate, and RAG-grounding signal.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/developer/analytics"
              className="btn-outline text-sm"
              data-testid="chat-analytics-btn"
              title="Raw chat-message log + CSV export"
            >
              📋 Raw Logs
            </Link>
          </div>
        </div>

        {/* Filters */}
        <section
          className="card p-4 mb-8 flex flex-wrap items-end gap-3"
          data-testid="filters-section"
        >
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Module</label>
            <select
              value={moduleId}
              onChange={(e) => setModuleId(e.target.value)}
              className="input-field"
              data-testid="module-filter-select"
            >
              <option value="">All modules</option>
              {modules.map((m) => (
                <option key={m.id} value={m.id}>
                  Module {m.order}: {m.title}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Window</label>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="input-field"
              data-testid="days-filter-select"
            >
              <option value={1}>Last 24 h</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last 365 days</option>
              <option value={0}>All time</option>
            </select>
          </div>
        </section>

        {err && (
          <div className="mb-6 p-3 rounded border border-red-200 bg-red-50 text-red-700 text-sm" data-testid="error-banner">
            {err}
          </div>
        )}

        {loading && !data && <div className="text-gray-500">Loading…</div>}

        {cohort && (
          <>
            {/* Cohort summary cards */}
            <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8" data-testid="cohort-cards">
              <StatCard
                label="Students"
                value={cohort.students}
                sub={`${cohort.total_messages} messages · ${cohort.total_sessions} sessions`}
                testid="card-students"
              />
              <StatCard
                label="Avg msgs / student"
                value={cohort.avg_messages_per_student}
                sub="depth proxy across cohort"
                testid="card-avg-msgs"
              />
              <StatCard
                label="Avg question chars"
                value={cohort.avg_question_chars}
                sub="higher = more elaborated prompts"
                testid="card-avg-chars"
              />
              <StatCard
                label="Repeat-rate"
                value={`${cohort.repeat_rate_pct}%`}
                sub="cached replies / total replies"
                testid="card-repeat-rate"
              />
              <StatCard
                label="RAG-grounded"
                value={`${cohort.rag_usage_pct}%`}
                sub="fresh replies citing chunks"
                testid="card-rag-usage"
              />
            </section>

            {/* Cohort scope distribution */}
            <section className="card p-5 mb-8" data-testid="cohort-scope-section">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-bold uppercase tracking-wider text-gray-700">
                  Cohort scope distribution
                </h2>
                <ScopeLegend />
              </div>
              <ScopeBar d={cohort.scope_distribution} />
              <div className="grid grid-cols-3 gap-3 mt-3 text-xs text-gray-600">
                <div>Topic — <strong>{cohort.scope_distribution.topic}</strong> ({cohort.scope_distribution.topic_pct}%)</div>
                <div>Course — <strong>{cohort.scope_distribution.course}</strong> ({cohort.scope_distribution.course_pct}%)</div>
                <div>Training — <strong>{cohort.scope_distribution.training}</strong> ({cohort.scope_distribution.training_pct}%)</div>
              </div>
            </section>

            {/* Per-student table */}
            <section className="card overflow-hidden" data-testid="students-table-section">
              <div className="px-5 py-3 border-b flex items-center justify-between">
                <h2 className="text-sm font-bold uppercase tracking-wider text-gray-700">
                  Per-student engagement ({sortedStudents.length})
                </h2>
                <span className="text-xs text-gray-500">Click a column header to sort. Click a row to drill down.</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="students-table">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <SortHeader id="email">Student</SortHeader>
                      <SortHeader id="messages" align="right">Messages</SortHeader>
                      <SortHeader id="sessions" align="right">Sessions</SortHeader>
                      <SortHeader id="messages_per_session" align="right">Msg/Session</SortHeader>
                      <SortHeader id="avg_question_chars" align="right">Avg chars</SortHeader>
                      <SortHeader id="repeat_rate_pct" align="right">Repeat %</SortHeader>
                      <SortHeader id="rag_usage_pct" align="right">RAG %</SortHeader>
                      <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-600">Scope mix</th>
                      <SortHeader id="last_active">Last active</SortHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedStudents.map((s) => (
                      <tr
                        key={s.email}
                        onClick={() => setDrillEmail(s.email)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setDrillEmail(s.email);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label={`Open timeline for ${s.name || s.email}`}
                        className="border-b hover:bg-emerald-50/40 cursor-pointer focus:outline-none focus:bg-emerald-50"
                        data-testid={`student-row-${s.email}`}
                      >
                        <td className="px-3 py-2.5">
                          <div className="font-semibold text-gray-900">{s.name || s.email}</div>
                          {s.name && <div className="text-[11px] text-gray-500">{s.email}</div>}
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono">{s.messages}</td>
                        <td className="px-3 py-2.5 text-right font-mono">{s.sessions}</td>
                        <td className="px-3 py-2.5 text-right font-mono">{s.messages_per_session}</td>
                        <td className="px-3 py-2.5 text-right font-mono">{s.avg_question_chars}</td>
                        <td className="px-3 py-2.5 text-right font-mono">{s.repeat_rate_pct}%</td>
                        <td className="px-3 py-2.5 text-right font-mono">{s.rag_usage_pct}%</td>
                        <td className="px-3 py-2.5 min-w-[180px]">
                          <ScopeBar d={s.scope_distribution} small />
                        </td>
                        <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">
                          {fmtDateTime(s.last_active)}
                        </td>
                      </tr>
                    ))}
                    {sortedStudents.length === 0 && (
                      <tr>
                        <td colSpan={9} className="px-3 py-8 text-center text-gray-500" data-testid="empty-students">
                          No student activity in this window.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </main>

      {drillEmail && (
        <TimelineModal
          email={drillEmail}
          moduleId={moduleId}
          onClose={() => setDrillEmail(null)}
        />
      )}
    </div>
  );
};

export default ChatEfficiencyDashboard;
